import { Typography, Tooltip, useTheme } from '@mui/material';
import {
  type GridRowData,
  type GridColumns,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { CustomDataGrid } from 'components';
import { colorTheme } from 'theme';

import { allocationDetails } from 'constants/constantText';
import { strings } from 'i18n/Strings';
import { useEffect, useState } from 'react';
import { getUnitAllocationInfoByOrderId } from 'service/FundManagement';
import { useSelector } from 'react-redux';

export interface UnitAllocationInfo {
  userInfo: {
    id: string | number;
    firstName: string;
    lastName: string;
    email: string;
    userId: string | number;
  };
  totalUnitsAllocated: number;
  percentageAllocation: number;
}

export interface OrderPlanMatrixPercentageDetails {
  userId: string | number;
  matrixPercentage?: number;
  ageGroup?: string;
}

export interface AllocationGridPropsType {
  orderId: number | null;
}

const AllocationDetailsNew = (props: AllocationGridPropsType): JSX.Element => {
  const { orderId } = props;
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);

  const [allocationData, setAllocationData] = useState<UnitAllocationInfo[]>(
    [],
  );

  const [pageCount, setPageCount] = useState(null);

  const pageInfo = useSelector(
    (state) => state?.pagination?.userAllocationInfo,
  );
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const getAllocationData = async () => {
    try {
      const response = await getUnitAllocationInfoByOrderId(
        Number(orderId),
        Number(pageInfo?.page) + 1,
        Number(pageInfo?.pageSize),
      );
      setAllocationData(response as UnitAllocationInfo[]);
      setPageCount(response?.result?.count);
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    void getAllocationData();
  }, [pageInfo?.page, pageInfo?.pageSize]);

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: allocationDetails.name,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.name}
        </Typography>
      ),
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row.userInfo.firstName}&nbsp;
            {row.userInfo.lastName}
          </Typography>
        );
      },
    },
    {
      field: allocationDetails.totalUnitsAllocated,
      flex: 1.5,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.totalAllocatedUnits}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {Number(row.totalUnitsAllocated).toFixed(7)}
        </Typography>
      ),
    },
    {
      field: allocationDetails.ageGroup,
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.ageGroup}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const ageGroup =
          allocationData?.userAllocationInfo[0]?.mmOrderUserPlanMatrixMapperInfo?.find(
            (val) => val.userId === row.userInfo.id,
          )?.ageGroup;
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {ageGroup ?? 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: allocationDetails.fundMatricesPercentage,
      flex: 1.3,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundMatricesPercentage}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const percentage =
          allocationData?.userAllocationInfo[0]?.mmOrderUserPlanMatrixMapperInfo?.find(
            (val) => val.userId === row.userInfo.id,
          )?.matrixPercentage;
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {percentage ? Number(percentage).toFixed(2) : 'N/A'}
          </Typography>
        );
      },
    },
    {
      field: allocationDetails.percentageAllocation,
      flex: 1.5,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.percentageAllocation}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {Number(row.percentageAllocation).toFixed(7)}
        </Typography>
      ),
    },
    {
      field: allocationDetails.email,
      flex: 1.4,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.email}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row.userInfo.email} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row.userInfo.email}
          </Typography>
        </Tooltip>
      ),
    },
  ];

  return (
    <CustomDataGrid
      columns={columns}
      rows={(allocationData?.result?.rows as GridRowData[]) || []}
      getRowId={(row: GridRowData) => `${String(row.userInfo.id)}`}
      getRowClassName={(params) =>
        params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
      }
      currentPage="userAllocationInfo"
      pageCount={pageCount}
    />
  );
};

export default AllocationDetailsNew;
